import { Formik } from 'formik';
import { useIntl } from 'gatsby-plugin-intl';
import React, { useRef } from 'react';
import { SpaceProps } from 'styled-system';

import { PropsOf } from 'types/jsx';

import { SearchBoxFormFields, SearchBoxFormValues } from '../../types';
import SearchButton from '../SearchButton';
import { UpdateButton } from '../UpdateButton';
import { FormSearch, InputSearch } from './styles';

type PropTypes = {
  placeholder?: string;
  placeholderId?: string;
  onSearch: (query: string) => void;
  inputClassName?: string;
  width?: string;
  validateForSubmit?: (values: SearchBoxFormValues) => boolean;
  allowClear?: boolean;
  isLoading?: boolean;
  onReloadClick?: () => void;
} & SpaceProps &
  PropsOf<typeof FormSearch>;

const SearchBox = ({
  onSearch,
  placeholder,
  placeholderId,
  inputClassName,
  width,
  validateForSubmit,
  allowClear = false,
  isLoading = false,
  onReloadClick,
  ...props
}: PropTypes): JSX.Element => {
  const { formatMessage } = useIntl();

  const defaultSearch = formatMessage({ id: 'search' });

  const placeholderNode = placeholder || defaultSearch;
  const placeholderText = placeholderId ? formatMessage({ id: placeholderId }) : placeholderNode;

  const dirty = useRef(false);

  return (
    <Formik<SearchBoxFormValues>
      onSubmit={({ query }): void => {
        dirty.current = false;
        onSearch(query || '');
      }}
      initialValues={{}}
    >
      {({ setFieldValue, values }): JSX.Element => (
        <FormSearch {...props}>
          <InputSearch
            name={SearchBoxFormFields.query}
            placeholder={placeholderText}
            className={inputClassName}
            width={width}
            onChange={(event): void => {
              dirty.current = true;
              setFieldValue(SearchBoxFormFields.query, event.currentTarget.value);
            }}
            allowClear={allowClear}
          />
          <SearchButton disabled={validateForSubmit ? !validateForSubmit(values) : isLoading} type="submit" />
          {onReloadClick && (
            <UpdateButton
              isLoading={isLoading}
              fn={(): void => {
                if (dirty.current) {
                  onSearch(values.query || '');
                } else {
                  onReloadClick();
                }
              }}
            />
          )}
        </FormSearch>
      )}
    </Formik>
  );
};

export default SearchBox;
